import { yupResolver } from '@hookform/resolvers/yup';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Input, Label } from '@/Components/shadcn';
import { cn } from '@/lib/utils';
import SubmissionError from '@/Pages/AcceptInvitation/Components/SumbissionError';
import UIStrings from '@/Utils/UIStrings';
import useAccountFormValidationScheme from './useAccountCreationFormValidation';

export interface AccountCreationFormValues {
    firstName: string;
    lastName: string;
    companyName: string;
}

export interface AccountCreationFormAPI {
    isLoading: boolean;
    isSubmitting: boolean;
    allowAccountSetup: boolean;
    submitHandler: (values: AccountCreationFormValues) => Promise<void>;
}

interface AccountCreationFormProps {
    initialCompanyName: string;
    initialFirstName: string;
    initialLastName: string;
    isSubmitting: boolean;
    submitHandler: (values: AccountCreationFormValues) => Promise<void>;
    isError?: boolean;
}

const AccountCreationForm = ({
    initialCompanyName,
    initialFirstName,
    initialLastName,
    submitHandler,
    isSubmitting,
    isError,
}: AccountCreationFormProps) => {
    const validationSchema = useAccountFormValidationScheme();
    const form = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { companyName: initialCompanyName, firstName: initialFirstName, lastName: initialLastName },
        mode: 'onSubmit',
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = form;

    return (
        <FormProvider {...form}>
            <form className="flex flex-col gap-6" onSubmit={handleSubmit(submitHandler)}>
                {isError && <SubmissionError />}
                <p className="text-foreground text-sm leading-relaxed">{UIStrings.ApplicantsWillSeeThisInEmails}</p>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-x-6">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="firstName">{UIStrings.FirstNameWithAesterisk}</Label>
                        <Input
                            id="firstName"
                            type="text"
                            autoComplete="given-name"
                            aria-invalid={Boolean(errors.firstName)}
                            className={cn(errors.firstName && 'border-destructive')}
                            {...register('firstName')}
                        />
                        {errors.firstName?.message && (
                            <p className="text-destructive text-sm" role="alert">
                                {errors.firstName.message}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="lastName">{UIStrings.LastNameWithAesterisk}</Label>
                        <Input
                            id="lastName"
                            type="text"
                            autoComplete="family-name"
                            aria-invalid={Boolean(errors.lastName)}
                            className={cn(errors.lastName && 'border-destructive')}
                            {...register('lastName')}
                        />
                        {errors.lastName?.message && (
                            <p className="text-destructive text-sm" role="alert">
                                {errors.lastName.message}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Label htmlFor="companyName">{`${UIStrings.CompanyName}*`}</Label>
                    <Input
                        id="companyName"
                        type="text"
                        autoComplete="organization"
                        disabled={initialCompanyName !== ''}
                        aria-invalid={Boolean(errors.companyName)}
                        className={cn(errors.companyName && 'border-destructive')}
                        {...register('companyName')}
                    />
                    {errors.companyName?.message && (
                        <p className="text-destructive text-sm" role="alert">
                            {errors.companyName.message}
                        </p>
                    )}
                </div>
                <Button type="submit" size="lg" className="mt-2 w-full tracking-wide" disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="mr-2 size-4 shrink-0 animate-spin" aria-hidden />}
                    {UIStrings.SaveAndContinue}
                </Button>
            </form>
        </FormProvider>
    );
};

export default AccountCreationForm;
